import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Input, Switch, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useFilterApplications } from '../hooks/useFilterApplications'
import {
  FilterApplicationsForm,
  FilterApplicationsSchema,
} from '../schemas/FilterApplicationsSchema'

export function DrawerFilterApplications() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-filter-applications')
  const [_, setFilters] = useFilterApplications()
  const hookForm = useForm<FilterApplicationsForm>({
    resolver: zodResolver(FilterApplicationsSchema()),
    defaultValues: {
      freeSearch: null,
      tag: null,
      requiresApiUser: null,
      requiresEntityAccount: null,
      isActive: false,
    },
  })

  const handleSubmit = (data: FilterApplicationsForm) => {
    setFilters(data)
    close()
  }

  return (
    <Drawer.Root id="drawer-filter-applications">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:applications.drawer_filter.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:applications.drawer_filter.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Input.Icon
              icon={<Search />}
              name="freeSearch"
              label={t(
                'sso:applications.drawer_filter.fields.free_search.label',
              )}
              placeholder={t(
                'sso:applications.drawer_filter.fields.free_search.placeholder',
              )}
            />

            <Input.Base
              name="tag"
              label={t('sso:applications.drawer_filter.fields.tag.label')}
              placeholder={t(
                'sso:applications.drawer_filter.fields.tag.placeholder',
              )}
            />

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="requiresApiUser">
                {t(
                  'sso:applications.drawer_filter.fields.requires_api_user.label',
                )}
              </Switch.Label>
              <Switch.Trigger name="requiresApiUser" />
            </Switch.Root>

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="requiresEntityAccount">
                {t(
                  'sso:applications.drawer_filter.fields.requires_entity_account.label',
                )}
              </Switch.Label>
              <Switch.Trigger name="requiresEntityAccount" />
            </Switch.Root>

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="isActive">
                {t('sso:applications.drawer_filter.fields.is_active.label')}
              </Switch.Label>
              <Switch.Trigger name="isActive" />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-between gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={close}
            type="button"
          >
            {t('sso:applications.drawer_filter.footer.button_close.label')}
          </Button.Root>

          <div className="flex items-center gap-4">
            <Button.Root
              variant="secondary"
              color="danger"
              onClick={() => hookForm.reset()}
            >
              {t('sso:applications.drawer_filter.footer.reset_button.label')}
            </Button.Root>

            <Button.Root
              variant="primary"
              color="brand"
              onClick={hookForm.handleSubmit(handleSubmit)}
              type="submit"
            >
              {t('sso:applications.drawer_filter.footer.submit_button.label')}
            </Button.Root>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
